import { MERGE_FIELDS, smsSegments, unknownMergeFields, type EmailBlock } from '@church/domain'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApi } from '../api-context.tsx'
import { useDebounced } from '../people/usePeople.ts'
import { messagingKeys } from './keys.ts'

/**
 * Messages: the list with delivery stats, the composer, and sender settings.
 * Sending is a request that returns at once; the worker delivers, and the
 * message's stats fill in as it does — the detail screen polls while sending.
 */

export function useMessages() {
  const api = useApi()
  const query = useInfiniteQuery({
    queryKey: messagingKeys.messages(),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.messages.get({
        query: { limit: 30, ...(pageParam ? { cursor: pageParam } : {}) },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
    // Sends in progress update their counts.
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) =>
        page.items.some((m) => m.status === 'sending' || m.status === 'queued'),
      )
        ? 3_000
        : false,
  })
  return {
    messages: query.data?.pages.flatMap((page) => page.items) ?? [],
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isLoading: query.isPending,
    error: query.error,
  }
}

export type MessageSummary = ReturnType<typeof useMessages>['messages'][number]

export function useMessage(id: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: messagingKeys.message(id ?? 'new'),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await api.api.v1.messages({ id: id ?? '' }).get()
      if (error) throw error
      return data
    },
    refetchInterval: (query) =>
      query.state.data?.status === 'sending' || query.state.data?.status === 'queued'
        ? 2_000
        : false,
  })
}

export function useMessageRecipients(id: string, status?: string) {
  const api = useApi()
  return useQuery({
    queryKey: messagingKeys.recipients(id, status),
    queryFn: async () => {
      const { data, error } = await api.api.v1
        .messages({ id })
        .recipients.get({ query: status ? { status } : {} })
      if (error) throw error
      return data
    },
  })
}

export type Channel = 'email' | 'sms'
export type AudienceKind = 'list' | 'group' | 'everyone'

export interface Draft {
  channel: Channel
  subject: string
  blocks: EmailBlock[]
  audienceKind: AudienceKind
  listId: string | null
  groupId: string | null
}

const EMPTY: Draft = {
  channel: 'email',
  subject: '',
  blocks: [{ type: 'paragraph', text: 'Hi {{firstName}},\n\n' }],
  audienceKind: 'everyone',
  listId: null,
  groupId: null,
}

export const MERGE_FIELD_OPTIONS = Object.entries(MERGE_FIELDS).map(([key, value]) => ({
  token: `{{${key}}}`,
  label: value.label,
}))

/**
 * The composer: the draft being written, what it will cost (SMS segments),
 * who it will reach right now, and how it renders for a real person — plus
 * save, send and schedule. Platform files render; every rule is here.
 */
export function useComposer(existing?: Draft & { id: string; status: string }) {
  const api = useApi()
  const queryClient = useQueryClient()
  const [id, setId] = useState<string | null>(existing?.id ?? null)
  const [draft, setDraft] = useState<Draft>(existing ?? EMPTY)
  const [previewPersonId, setPreviewPersonId] = useState<string | null>(null)

  const smsBody = draft.blocks[0]?.type === 'paragraph' ? draft.blocks[0].text : ''
  const segments =
    draft.channel === 'sms' ? smsSegments(`${smsBody}\nReply STOP to opt out.`) : null
  const unknown = [
    ...new Set([
      ...unknownMergeFields(draft.subject),
      ...draft.blocks.flatMap((b) => ('text' in b ? unknownMergeFields(b.text) : [])),
    ]),
  ]

  const audienceInput = useDebounced(
    {
      channel: draft.channel,
      audienceKind: draft.audienceKind,
      listId: draft.listId,
      groupId: draft.groupId,
    },
    300,
  )
  const audienceReady =
    audienceInput.audienceKind === 'everyone' ||
    (audienceInput.audienceKind === 'list' && Boolean(audienceInput.listId)) ||
    (audienceInput.audienceKind === 'group' && Boolean(audienceInput.groupId))
  const audience = useQuery({
    queryKey: messagingKeys.audience(audienceInput),
    enabled: audienceReady,
    queryFn: async () => {
      const { data, error } = await api.api.v1.messages.audience.post(audienceInput)
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })

  const body = () => ({
    channel: draft.channel,
    subject: draft.channel === 'email' ? draft.subject : null,
    blocks: draft.blocks,
    audienceKind: draft.audienceKind,
    listId: draft.listId,
    groupId: draft.groupId,
  })

  const previewInput = useDebounced({ message: body(), personId: previewPersonId }, 400)
  const preview = useQuery({
    queryKey: messagingKeys.preview(previewInput),
    enabled: unknown.length === 0 && (draft.channel === 'sms' || draft.subject.trim().length > 0),
    queryFn: async () => {
      const { data, error } = await api.api.v1.messages.preview.post(previewInput)
      if (error) throw error
      return data
    },
    placeholderData: (previous) => previous,
  })

  const save = useMutation({
    mutationFn: async () => {
      const result = id
        ? await api.api.v1.messages({ id }).put(body())
        : await api.api.v1.messages.post(body())
      if (result.error) throw result.error
      return result.data
    },
    onSuccess: (data) => {
      setId(data.id)
      queryClient.setQueryData(messagingKeys.message(data.id), data)
      void queryClient.invalidateQueries({ queryKey: messagingKeys.messages() })
    },
  })

  const send = useMutation({
    mutationFn: async (scheduledAt: string | null) => {
      const saved = await save.mutateAsync()
      const { data, error } = await api.api.v1.messages({ id: saved.id }).send.post({ scheduledAt })
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(messagingKeys.message(data.id), data)
      void queryClient.invalidateQueries({ queryKey: messagingKeys.messages() })
    },
  })

  const setBlock = (index: number, block: EmailBlock) =>
    setDraft((d) => ({ ...d, blocks: d.blocks.map((b, i) => (i === index ? block : b)) }))

  const audienceChosen =
    draft.audienceKind === 'everyone' ||
    (draft.audienceKind === 'list' ? Boolean(draft.listId) : Boolean(draft.groupId))
  const hasContent =
    draft.channel === 'sms'
      ? smsBody.trim().length > 0
      : draft.subject.trim().length > 0 && draft.blocks.length > 0

  return {
    id,
    draft,
    setChannel: (channel: Channel) =>
      setDraft((d) => ({
        ...d,
        channel,
        // A text is one paragraph: keep the words, drop the layout.
        blocks:
          channel === 'sms'
            ? [
                {
                  type: 'paragraph',
                  text: d.blocks
                    .flatMap((b) => ('text' in b ? [b.text] : []))
                    .join('\n')
                    .slice(0, 1600),
                },
              ]
            : d.blocks,
      })),
    setSubject: (subject: string) => setDraft((d) => ({ ...d, subject })),
    setSmsBody: (text: string) =>
      setDraft((d) => ({ ...d, blocks: [{ type: 'paragraph', text }] })),
    setAudience: (audienceKind: AudienceKind, ref: string | null = null) =>
      setDraft((d) => ({
        ...d,
        audienceKind,
        listId: audienceKind === 'list' ? ref : null,
        groupId: audienceKind === 'group' ? ref : null,
      })),
    setBlock,
    addBlock: (block: EmailBlock) => setDraft((d) => ({ ...d, blocks: [...d.blocks, block] })),
    removeBlock: (index: number) =>
      setDraft((d) => ({ ...d, blocks: d.blocks.filter((_, i) => i !== index) })),
    moveBlock: (index: number, by: -1 | 1) =>
      setDraft((d) => {
        const target = index + by
        if (target < 0 || target >= d.blocks.length) return d
        const blocks = [...d.blocks]
        const [moved] = blocks.splice(index, 1)
        if (moved) blocks.splice(target, 0, moved)
        return { ...d, blocks }
      }),
    smsBody,
    segments,
    unknownFields: unknown,
    audience: audience.data ?? null,
    audienceLoading: audience.isFetching,
    preview: preview.data ?? null,
    previewError: preview.error,
    previewPersonId,
    setPreviewPersonId,
    canSend:
      unknown.length === 0 && audienceChosen && hasContent && (audience.data?.reachable ?? 0) > 0,
    save,
    send,
  }
}

export function useCancelMessage() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.api.v1.messages({ id }).cancel.post()
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(messagingKeys.message(data.id), data)
      void queryClient.invalidateQueries({ queryKey: messagingKeys.messages() })
    },
  })
}

/** Sends a finished message again to its failed recipients (after an outage). */
export function useRetryFailedRecipients() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.api.v1.messages({ id })['retry-failed'].post()
      if (error) throw error
      return data
    },
    // The message, its counts and every filtered recipient list change.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: messagingKeys.all() }),
  })
}

/**
 * A template as a staff list shows it: merge fields as their labels
 * ("Stay safe, ‹First name›!"), not as braces cut off mid-tag.
 */
export function templateLabel(template: string, max = 90): string {
  const labelled = template.replace(/\{\{\s*([^{}|]*?)\s*(?:\|[^{}]*)?\}\}/g, (_, name: string) => {
    const field = MERGE_FIELDS[name.trim() as keyof typeof MERGE_FIELDS]
    return `‹${field?.label ?? name.trim()}›`
  })
  const flat = labelled.replace(/\s+/g, ' ').trim()
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat
}

/** What a message is called in lists: its subject, or the start of its text. */
export function messageTitle(message: {
  channel: string
  subject: string | null
  blocks: EmailBlock[]
}): string {
  if (message.channel === 'email') return templateLabel(message.subject ?? '') || '(no subject)'
  const first = message.blocks[0]
  return first && 'text' in first ? templateLabel(first.text) : 'Text'
}

/** Open, click and unsubscribe rates as whole percentages of delivered. */
export function messageRates(stats: {
  sent: number
  opened: number
  clicked: number
  unsubscribed: number
  bounced: number
}) {
  const pct = (n: number) => (stats.sent > 0 ? Math.round((n / stats.sent) * 100) : 0)
  return {
    opened: pct(stats.opened),
    clicked: pct(stats.clicked),
    unsubscribed: pct(stats.unsubscribed),
    bounced: pct(stats.bounced),
  }
}

// ---- settings and keywords -----------------------------------------------------------

export function useMessagingSettings() {
  const api = useApi()
  const queryClient = useQueryClient()
  const settings = useQuery({
    queryKey: messagingKeys.settings(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.messaging.settings.get()
      if (error) throw error
      return data
    },
  })
  const update = useMutation({
    mutationFn: async (body: {
      fromName?: string | null
      smsNumber?: string | null
      smsHelpText?: string | null
    }) => {
      const { data, error } = await api.api.v1.messaging.settings.put(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(messagingKeys.settings(), data),
  })
  const keywords = useQuery({
    queryKey: messagingKeys.keywords(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.messaging.keywords.get()
      if (error) throw error
      return data
    },
  })
  const addKeyword = useMutation({
    mutationFn: async (body: { keyword: string; reply: string }) => {
      const { data, error } = await api.api.v1.messaging.keywords.post(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(messagingKeys.keywords(), data),
  })
  const updateKeyword = useMutation({
    mutationFn: async ({ id, ...body }: { id: string; keyword?: string; reply?: string }) => {
      const { data, error } = await api.api.v1.messaging.keywords({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(messagingKeys.keywords(), data),
  })
  const removeKeyword = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.api.v1.messaging.keywords({ id }).delete()
      if (error) throw error
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(messagingKeys.keywords(), data),
  })
  return { settings, update, keywords, addKeyword, updateKeyword, removeKeyword }
}

// ---- a person's choices --------------------------------------------------------------

export function usePersonSubscriptions(personId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: messagingKeys.subscriptions(personId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.people({ id: personId }).subscriptions.get()
      if (error) throw error
      return data
    },
  })
  const update = useMutation({
    mutationFn: async (body: { all?: boolean; email?: boolean; sms?: boolean }) => {
      const { data, error } = await api.api.v1.people({ id: personId }).subscriptions.put(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(messagingKeys.subscriptions(personId), data),
  })
  return { query, update }
}

/** The public preference center and unsubscribe page, addressed by the link's token. */
export function usePreferenceCenter(ref: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const preferences = useQuery({
    queryKey: messagingKeys.preferences(ref),
    queryFn: async () => {
      const { data, error } = await api.api.v1.public.messaging.preferences({ ref }).get()
      if (error) throw error
      return data
    },
    retry: false,
  })
  const update = useMutation({
    mutationFn: async (body: { all?: boolean; email?: boolean; sms?: boolean }) => {
      const { data, error } = await api.api.v1.public.messaging.preferences({ ref }).put(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(messagingKeys.preferences(ref), data),
  })
  const unsubscribe = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.public.messaging.unsubscribe({ ref }).post()
      if (error) throw error
      return data
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: messagingKeys.preferences(ref) }),
  })
  return { preferences, update, unsubscribe }
}
